import type {
  AppResource,
  BulkOperationKind,
  BulkPreflightResource,
  BulkRunCellResource,
  BulkRunDetail,
  BulkRunResource,
  CandidateResource,
  CostSummaryResource,
  ExperimentResource,
} from '@studio/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { isInWork } from '../app/Archive.js';
import { assetTypeLabel, targetLabel } from '../app/history.js';
import { api, ApiError } from '../shell/api.js';
import { EmptyState, Failure } from '../shell/EmptyState.js';
import { Link, useNavigate, type Surface } from '../shell/router.js';
import { useResourceEvents } from '../shell/stream.js';
import { useAsync } from '../shell/useAsync.js';
import { cellStateLabel, money, progressLine, runStateLabel, spendLine, spentOverPeriod } from './matrix.js';
import { Preflight, seedFrom, type PreflightSeed } from './Preflight.js';

interface Workspace {
  readonly apps: readonly AppResource[];
  readonly experiments: readonly ExperimentResource[];
  readonly runs: readonly BulkRunResource[];
}

const EMPTY_SEED: PreflightSeed = {
  operationKind: 'firstSpread',
  appIds: [],
  targets: [],
  assetTypes: [],
  sharedBrief: '',
  exceptions: {},
  sourceExperimentId: null,
  sourceCandidateId: null,
};

/**
 * Bulk Runs.
 *
 * Two ways in and one mechanism behind both: `Создать много` expands a matrix into new
 * Experiments, and `Repeat for...` carries one Experiment's direction to other Apps or
 * Targets as a reference. Finished runs stay here as a light list - somewhere to open
 * the results or run the matrix again - and never become a campaign system.
 */
function BulkRuns(): ReactNode {
  const [kind, setKind] = useState<BulkOperationKind>('firstSpread');
  const [sourceExperimentId, setSourceExperimentId] = useState('');
  const [sourceCandidateId, setSourceCandidateId] = useState('');
  const [packing, setPacking] = useState<readonly string[]>([]);
  const navigate = useNavigate();

  const loaded = useAsync<Workspace>(async () => {
    const [apps, experiments, runs] = await Promise.all([api.listApps(), api.listExperiments(), api.listBulkRuns()]);
    // A new run is for Apps still worked on; the runs already made keep every App they had.
    return { apps: apps.apps.filter(isInWork), experiments: experiments.experiments, runs: runs.runs };
  }, []);

  const candidates = useAsync<readonly CandidateResource[]>(async () => {
    if (sourceExperimentId === '') return [];
    return (await api.readExperiment(sourceExperimentId)).candidates;
  }, [sourceExperimentId]);

  const opened = useCallback((runId: string) => navigate(`/bulk-runs/${runId}`), [navigate]);

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Bulk Runs</p>
        <Failure message={loaded.error} onRetry={loaded.reload} />
      </section>
    );
  }
  if (loaded.value === null) return <section className="page" aria-busy="true" />;

  const { apps, experiments, runs } = loaded.value;
  if (apps.length === 0) {
    return (
      <section className="page">
        <p className="eyebrow">Bulk Runs</p>
        <h1>Bulk Runs ещё нет.</h1>
        <p className="note">
          Bulk Run разворачивает Apps, Targets и Asset Types в независимые Experiments. Добавьте App в Workspace —
          и появится что запускать.
        </p>
      </section>
    );
  }

  /**
   * R156. The third thing a run can do, and the one no screen ever asked for.
   *
   * An export-preparation cell creates nothing: it packs an Experiment that already
   * exists, using the Play Test Pair a person chose inside it. So its rows are named
   * Experiments rather than a matrix, and the shared Brief has nothing to do with it.
   */
  const seed: PreflightSeed =
    kind === 'repeat'
      ? {
          ...EMPTY_SEED,
          operationKind: 'repeat',
          sourceExperimentId: sourceExperimentId === '' ? null : sourceExperimentId,
          sourceCandidateId: sourceCandidateId === '' ? null : sourceCandidateId,
        }
      : kind === 'exportPreparation'
        ? {
            ...EMPTY_SEED,
            operationKind: 'exportPreparation',
            pinnedRows: packing.flatMap((experimentId) => {
              const experiment = experiments.find((one) => one.id === experimentId);
              return experiment === undefined
                ? []
                : [
                    {
                      appId: experiment.appId,
                      target: experiment.target,
                      assetType: experiment.assetType,
                      experimentId: experiment.id,
                    },
                  ];
            }),
          }
        : EMPTY_SEED;

  return (
    <section className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Bulk Runs · только ход запуска и стоимость</p>
          <h1>Много Experiments. По-прежнему много ясных тестов.</h1>
        </div>
      </div>

      <fieldset>
        <legend>Что делает этот run</legend>
        <label>
          <input type="radio" name="kind" checked={kind === 'firstSpread'} onChange={() => setKind('firstSpread')} />
          Создать много
        </label>
        <label>
          <input type="radio" name="kind" checked={kind === 'repeat'} onChange={() => setKind('repeat')} />
          Repeat for…
        </label>
        <label>
          <input
            type="radio"
            name="kind"
            checked={kind === 'exportPreparation'}
            onChange={() => setKind('exportPreparation')}
          />
          Подготовить exports
        </label>
      </fieldset>

      {kind === 'repeat' ? (
        <div className="repeat-form">
          <label htmlFor="repeat-source">Повторить направление</label>
          <select
            id="repeat-source"
            value={sourceExperimentId}
            onChange={(event) => {
              setSourceExperimentId(event.target.value);
              setSourceCandidateId('');
            }}
          >
            <option value="">Выберите Experiment</option>
            {experiments.map((experiment) => (
              <option key={experiment.id} value={experiment.id}>
                {`${assetTypeLabel(experiment.assetType)} · ${targetLabel(experiment.target)} · ${
                  experiment.hypothesis ?? experiment.brief.slice(0, 60)
                }`}
              </option>
            ))}
          </select>
          <label htmlFor="repeat-candidate">Взять этот Candidate как ориентир</label>
          <select
            id="repeat-candidate"
            value={sourceCandidateId}
            disabled={sourceExperimentId === ''}
            onChange={(event) => setSourceCandidateId(event.target.value)}
          >
            <option value="">Только Brief</option>
            {(candidates.value ?? []).map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.headline}
              </option>
            ))}
          </select>
          <p className="note">
            Назначение получает свой новый Experiment. Candidate приходит как ориентир для креатива, а не как
            копия, родитель или ветка.
          </p>
        </div>
      ) : null}

      {kind !== 'exportPreparation' ? null : (
        <fieldset>
          <legend>Experiments для упаковки</legend>
          <p className="note">
            Каждая строка упаковывает названный в ней Experiment, используя Play Test Pair, выбранный внутри него. Ничего
            не создаётся и ничего не генерируется; Experiment без выбранной пары так и скажет в строке.
          </p>
          {experiments.length === 0 ? (
            <p className="note">Пока нет Experiment для упаковки.</p>
          ) : (
            experiments.map((experiment) => (
              <label key={experiment.id}>
                <input
                  type="checkbox"
                  checked={packing.includes(experiment.id)}
                  onChange={() =>
                    setPacking((current) =>
                      current.includes(experiment.id)
                        ? current.filter((one) => one !== experiment.id)
                        : [...current, experiment.id],
                    )
                  }
                />
                {/* The App leads: a repeat carries its source's Hypothesis, so two
                    Experiments on two Apps can otherwise read as the same line. */}
                {`${apps.find((app) => app.id === experiment.appId)?.displayName ?? 'Неизвестный App'} · ${assetTypeLabel(
                  experiment.assetType,
                )} · ${targetLabel(experiment.target)} · ${experiment.hypothesis ?? experiment.brief.slice(0, 60)}`}
              </label>
            ))
          )}
        </fieldset>
      )}

      <Preflight
        key={`${kind}:${sourceExperimentId}:${sourceCandidateId}`}
        apps={apps}
        seed={seed}
        onLaunched={opened}
      />

      <h2>Недавние Bulk Runs</h2>
      <WorkspaceSpend />
      {runs.length === 0 ? (
        <EmptyState title="Bulk Runs ещё не запускали." note="Запустите выше — и он появится здесь, чтобы открыть снова или повторить." />
      ) : (
        <ul className="run-list" aria-label="Недавние Bulk Runs">
          {runs.map((run) => (
            <li key={run.id}>
              <Link to={`/bulk-runs/${run.id}`}>{`${runStateLabel(run.state)} · ${progressLine(run.readyCount, run.cellCount)}`}</Link>
              <span className="note">{` ${spendLine(run.spentUsd, run.ceilingUsd)}`}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Compact spend over a period the operator chooses.
 *
 * Two numbers and the window they cover. There are no tokens here, no balance, no
 * invoice and no report: the question this answers is "what has this cost", and a
 * finance dashboard would answer a question nobody asked.
 */
function WorkspaceSpend(): ReactNode {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const costs = useAsync<CostSummaryResource>(
    () => api.readCosts({ ...(from === '' ? {} : { from }), ...(to === '' ? {} : { to }) }),
    [from, to],
  );

  return (
    <div className="spend-filter">
      <label htmlFor="spend-from">Траты с</label>
      <input id="spend-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
      <label htmlFor="spend-to">по</label>
      <input id="spend-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
      {costs.value === null ? null : <p>{spentOverPeriod(costs.value)}</p>}
    </div>
  );
}

/**
 * One run, while it runs.
 *
 * Everything on this page is a count of something that has happened: rows ready, money
 * committed, rows by App and the five states a cell can be in. There is no finishing
 * time here and there is no place to put one.
 */
function BulkRun({ bulkRunId }: { bulkRunId: string }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [again, setAgain] = useState<BulkPreflightResource | null>(null);
  const navigate = useNavigate();

  const loaded = useAsync<BulkRunDetail>(() => api.readBulkRun(bulkRunId), [bulkRunId]);
  const { set, reload } = loaded;

  const apps = useAsync<readonly AppResource[]>(async () => (await api.listApps()).apps, []);
  const costs = useAsync<CostSummaryResource>(() => api.readCosts({ bulkRunId }), [bulkRunId]);

  /**
   * The live half.
   *
   * A cell becomes Ready because the Candidates its Experiment asked for arrived, and
   * those arrive as Candidate Slot events on the shared stream. Reading the run again
   * is what turns them into row states, so this listener is what makes the page move
   * without a reload.
   */
  useResourceEvents((event) => {
    const mine =
      (event.resource === 'bulkRun' && event.id === bulkRunId) ||
      event.resource === 'bulkRunCell' ||
      event.resource === 'candidateSlot' ||
      event.resource === 'job';
    if (mine) reload();
  });

  const command = useCallback(
    async (run: () => Promise<BulkRunDetail>) => {
      setBusy(true);
      setFailed(null);
      try {
        set(await run());
      } catch (caught) {
        setFailed(caught instanceof ApiError ? caught.message : 'Не удалось сделать.');
      } finally {
        setBusy(false);
      }
    },
    [set],
  );

  if (loaded.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Bulk Run</p>
        <Failure message={loaded.error} onRetry={reload} />
      </section>
    );
  }
  if (loaded.value === null) return <section className="page" aria-busy="true" />;

  const { run, cells, byApp } = loaded.value;
  const stoppedForGood = run.state === 'stopped' || run.state === 'completed';

  return (
    <section className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Bulk Run · временная группа выполнения</p>
          <h1>{runStateLabel(run.state)}</h1>
          <p className="note">{progressLine(run.readyCount, run.cellCount)}</p>
          <p className="note">{`Траты / потолок: ${spendLine(run.spentUsd, run.ceilingUsd)}`}</p>
          {costs.value === null ? null : <p className="note">{spentOverPeriod(costs.value)}</p>}
        </div>
        <Link to="/bulk-runs">Bulk Runs</Link>
      </div>

      {run.allowanceNeededUsd === null || run.allowanceNeededUsd <= 0 ? null : (
        <p role="status">{`Строкам, которые ещё не стартовали, нужно ещё ${money(run.allowanceNeededUsd)}, чтобы закончить.`}</p>
      )}

      {failed === null ? null : (
        <p className="failure" role="alert">
          {failed}
        </p>
      )}

      <div className="row-actions">
        {run.state === 'paused' ? (
          <button type="button" className="secondary" disabled={busy} onClick={() => void command(() => api.resumeBulkRun(run.id))}>
            Продолжить
          </button>
        ) : (
          <button
            type="button"
            className="secondary"
            disabled={busy || stoppedForGood}
            onClick={() => void command(() => api.pauseBulkRun(run.id))}
          >
            Пауза
          </button>
        )}
        <button
          type="button"
          className="secondary"
          disabled={busy || run.state !== 'ceilingReached'}
          onClick={() => void command(() => api.continueBulkRun(run.id))}
        >
          Продолжить с допуском
        </button>
        <button
          type="button"
          className="ghost"
          disabled={busy || stoppedForGood}
          onClick={() => void command(() => api.stopBulkRun(run.id))}
        >
          Остановить оставшиеся
        </button>
        <button
          type="button"
          className="ghost"
          disabled={busy || run.needsAttentionCount === 0}
          onClick={() => void command(() => api.retryBulkRun(run.id))}
        >
          Повторить неудачные
        </button>
        <button
          type="button"
          className="ghost"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            api
              .rerunBulkRun(run.id)
              .then(setAgain)
              .catch(() => setFailed('Не удалось восстановить матрицу.'))
              .finally(() => setBusy(false));
          }}
        >
          Запустить снова
        </button>
      </div>

      <ul className="run-groups" aria-label="Строки по App">
        {byApp.map((group) => (
          <li key={group.appId}>
            <p>{`${group.appName} · ${progressLine(group.readyCount, group.cellCount)}`}</p>
            <ul className="run-list" aria-label={`Строки · ${group.appName}`}>
              {cells
                .filter((cell) => cell.appId === group.appId)
                .map((cell) => (
                  <Row key={cell.id} cell={cell} />
                ))}
            </ul>
          </li>
        ))}
      </ul>

      {again === null || apps.value === null ? null : (
        <Preflight apps={apps.value} seed={seedFrom(again)} onLaunched={(id) => navigate(`/bulk-runs/${id}`)} />
      )}
    </section>
  );
}

/** One row: what it is, where it got to, and the way in when it needs a person. */
function Row({ cell }: { cell: BulkRunCellResource }): ReactNode {
  return (
    <li className="run-row">
      <span>{`${assetTypeLabel(cell.assetType)} · ${targetLabel(cell.target)} · ${cellStateLabel(cell.state)}`}</span>
      {cell.rowException === null ? null : <span className="note">{` ${cell.rowException}`}</span>}
      {cell.message === null ? null : <span className="note">{` ${cell.message}`}</span>}
      {cell.experimentId === null ? null : (
        <Link to={`/experiments/${cell.experimentId}/explore`}>
          {`Открыть Experiment · ${cell.appName} ${assetTypeLabel(cell.assetType)}`}
        </Link>
      )}
    </li>
  );
}

export const surfaces: readonly Surface[] = [
  { id: 'bulk-runs', path: '/bulk-runs', title: 'Bulk Runs', render: () => <BulkRuns /> },
  {
    id: 'bulk-run',
    path: '/bulk-runs/:bulkRunId',
    title: 'Bulk Run',
    render: (params) => <BulkRun bulkRunId={params.bulkRunId ?? ''} />,
  },
];
